import React from 'react';
import { Field } from '@ontruck/orc';
import { PropTypes } from 'prop-types';
import { compose, pure } from 'recompose';

import ResponsiveModal from 'components/ResponsiveModal';
import SuggestedTimes from 'components/form/SuggestedTimes';
import FieldError from 'components/form/FieldError';
import useFormState from 'hooks/useFormState';

// TODo move this maskedProps
const maskedProps = {
  mask: '99:99',
  maskChar: null,
  beforeMaskedValueChange: (newState, oldState, userInput) => {
    const { value } = newState;
    const { selection } = newState;

    if (userInput === ':' && value.length === 1 && value === oldState.value) {
      const newValue = `0${value}:`;
      return {
        value: newValue,
        selection: {
          start: newValue.length,
          end: newValue.length,
        },
      };
    }

    if (userInput) {
      return { value, selection };
    }

    if (value.length > oldState.value.length) {
      return { value, selection: { start: value.length, end: value.length } };
    }

    return { value, selection };
  },
};

const enhance = compose(pure);

const TimePicker = ({
  className,
  handleChangeFrom,
  handleChangeTo,
  handleSelectFrom,
  handleSelectTo,
  fromValue,
  toValue,
  handleBlurFrom,
  handleFocusFrom,
  handleBlurTo,
  handleFocusTo,
  fromId,
  toId,
  fromWarning,
  fromError,
  toWarning,
  toError,
  shipmentKey,
  stopType,
  timeFieldFrom,
  timeFieldTo,
  responsiveModalFromRef,
  responsiveModalToRef,
  disabled,

  toLabel,
  fromLabel,
  errorLabel,

  isFormFocusedAndNoTyped,
  isToFocusedAndNoTyped,
  typeOfOrder,
  t,
}) => {
  const fieldFrom = 'date_from';
  const fieldTo = 'date_to';
  const stopId = `${shipmentKey}-${stopType}`;
  const { fieldHasError: fromFieldError } = useFormState(
    stopId,
    fieldFrom,
    fromValue
  );
  const { fieldHasError: toFieldError } = useFormState(
    stopId,
    fieldTo,
    toValue
  );
  const hasToError = !!toFieldError || !!toError ? 'error' : 'not-error';
  const hasFromError = !!fromFieldError || !!fromError ? 'error' : 'not-error';

  return (
    <div className={className}>
      <div
        className={`fieldContainer ${
          stopType === 'dropoff' ? 'floating-large' : ''
        }`}
      >
        {!!fromError && <FieldError>{errorLabel}</FieldError>}

        <ResponsiveModal
          modalRef={responsiveModalFromRef}
          refProperty="maskedRef"
          content={
            <SuggestedTimes
              shouldFilter={!isFormFocusedAndNoTyped}
              value={fromValue}
              onChange={handleSelectFrom}
              shipmentKey={shipmentKey}
              stopType={stopType}
              timeField={timeFieldFrom}
              typeOfOrder={typeOfOrder}
              hasHelpText={stopType === 'dropoff'}
            />
          }
          disableModalOnMobile
        >
          <Field
            type="text"
            dataE2E={`${stopType}-time-from`}
            className={`${hasFromError}`}
            id={fromId}
            name={fromId}
            value={fromValue}
            maskedProps={disabled ? null : maskedProps}
            label={fromLabel}
            persistedPlaceholder={t('mask')}
            onChange={handleChangeFrom}
            onBlur={handleBlurFrom}
            onFocus={handleFocusFrom}
            size="L"
            fullWidth
            invalid={!!fromWarning}
            // error={dateFromError || !!fromError}
            disabled={disabled}
          />
        </ResponsiveModal>
      </div>
      <div className="fieldContainer">
        {!!toError && <FieldError>{errorLabel}</FieldError>}
        <ResponsiveModal
          modalRef={responsiveModalToRef}
          refProperty="maskedRef"
          content={
            <SuggestedTimes
              shouldFilter={!isToFocusedAndNoTyped}
              value={toValue}
              onChange={handleSelectTo}
              shipmentKey={shipmentKey}
              stopType={stopType}
              timeField={timeFieldTo}
              typeOfOrder={typeOfOrder}
            />
          }
          disableModalOnMobile
        >
          <Field
            type="text"
            dataE2E={`${stopType}-time-to`}
            className={`${hasToError}`}
            value={toValue}
            id={toId}
            name={toId}
            maskedProps={disabled ? null : maskedProps}
            label={toLabel}
            persistedPlaceholder={t('mask')}
            onChange={handleChangeTo}
            onBlur={handleBlurTo}
            onFocus={handleFocusTo}
            fullWidth
            size="L"
            invalid={!!toWarning}
            // error={dateToError || !!toError}
            disabled={disabled}
          />
        </ResponsiveModal>
      </div>
    </div>
  );
};

TimePicker.propTypes = {
  className: PropTypes.string,

  handleChangeFrom: PropTypes.func.isRequired,
  handleChangeTo: PropTypes.func.isRequired,

  handleSelectFrom: PropTypes.func.isRequired,
  handleSelectTo: PropTypes.func.isRequired,

  fromId: PropTypes.string.isRequired,
  toId: PropTypes.string.isRequired,

  fromValue: PropTypes.string,
  toValue: PropTypes.string,

  handleBlurFrom: PropTypes.func.isRequired,
  handleBlurTo: PropTypes.func.isRequired,

  handleFocusFrom: PropTypes.func.isRequired,
  handleFocusTo: PropTypes.func.isRequired,

  fromWarning: PropTypes.string,
  fromError: PropTypes.string,

  toWarning: PropTypes.string,
  toError: PropTypes.string,

  timeFieldFrom: PropTypes.string.isRequired,
  timeFieldTo: PropTypes.string.isRequired,

  shipmentKey: PropTypes.string.isRequired,
  stopType: PropTypes.string.isRequired,

  responsiveModalFromRef: PropTypes.func.isRequired,
  responsiveModalToRef: PropTypes.func.isRequired,

  disabled: PropTypes.bool,

  toLabel: PropTypes.string.isRequired,
  fromLabel: PropTypes.string.isRequired,
  errorLabel: PropTypes.string.isRequired,

  isFormFocusedAndNoTyped: PropTypes.bool.isRequired,
  isToFocusedAndNoTyped: PropTypes.bool.isRequired,

  typeOfOrder: PropTypes.string,

  t: PropTypes.func,
};

TimePicker.defaultProps = {
  className: '',
  fromValue: '',
  toValue: '',
  fromWarning: '',
  toWarning: '',
  fromError: '',
  toError: '',
  typeOfOrder: '',
  t: (t) => t,
};

export default enhance(TimePicker);
